/**
 * Utilidades para manejo de fechas y conversión de Timestamps de Firebase
 */

import { formatDate as coreFormatDate } from '@/utils/format/formatCore'
import { getFormatSettings } from '@/composables/useFormat'

/**
 * Convierte un valor que puede ser Date, Timestamp de Firebase, o string a Date
 * 
 * @param value - Valor a convertir (Date, Timestamp, string, o null)
 * @returns Date válido o null si no se puede convertir
 * 
 * @example
 * // Timestamp de Firebase
 * convertToDate({ seconds: 1638316800, nanoseconds: 0 }) // Date
 * 
 * // String ISO
 * convertToDate("2023-11-28T10:00:00Z") // Date
 * 
 * // Date object
 * convertToDate(new Date()) // Date
 */
export function convertToDate(value: any): Date | null {
  if (!value) return null
  
  // Si ya es Date, retornar
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }
  
  // Si es Timestamp de Firebase (formato { seconds, nanoseconds }) o su
  // serialización JSON, que es como llega desde las Cloud Functions ({ _seconds })
  if (typeof value === 'object' && ('seconds' in value || '_seconds' in value)) {
    return new Date((value.seconds ?? value._seconds) * 1000)
  }
  
  // Si tiene método toDate (Firestore Timestamp)
  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return value.toDate()
  }
  
  // Intentar parsear como string
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value)
    return isNaN(date.getTime()) ? null : date
  }
  
  return null
}

/**
 * Normaliza una fecha al inicio del día (00:00:00.000) en UTC
 * Útil para comparaciones de fechas sin considerar la hora
 * 
 * @param date - Fecha a normalizar
 * @returns Date normalizado o null
 */
export function setStartOfDay(date: Date | null): Date | null {
  if (!date) return null
  const normalized = new Date(date)
  normalized.setUTCHours(0, 0, 0, 0)
  return normalized
}

/**
 * Normaliza una fecha al final del día (23:59:59.999) en UTC
 * Útil para comparaciones de fechas sin considerar la hora
 * 
 * @param date - Fecha a normalizar
 * @returns Date normalizado o null
 */
export function setEndOfDay(date: Date | null): Date | null {
  if (!date) return null
  const normalized = new Date(date)
  normalized.setUTCHours(23, 59, 59, 999)
  return normalized
}

/**
 * Verifica si una fecha está dentro de un rango
 * 
 * @param date - Fecha a verificar
 * @param from - Fecha inicial del rango
 * @param to - Fecha final del rango
 * @returns true si la fecha está en el rango
 */
export function isDateInRange(date: any, from: any, to: any): boolean {
  const dateObj = convertToDate(date)
  if (!dateObj) return false
  
  const fromDate = from ? setStartOfDay(convertToDate(from)) : null
  const toDate = to ? setEndOfDay(convertToDate(to)) : null
  const normalizedDate = setStartOfDay(dateObj)
  
  if (!normalizedDate) return false
  
  if (fromDate && toDate) {
    return normalizedDate >= fromDate && normalizedDate <= toDate
  }
  if (fromDate) {
    return normalizedDate >= fromDate
  }
  if (toDate) {
    return normalizedDate <= toDate
  }
  
  return true
}

/**
 * Formatea una fecha al formato YYYY-MM-DD usando UTC
 * 
 * @param date - Fecha a formatear
 * @returns String en formato YYYY-MM-DD o null
 */
export function formatDateYYYYMMDD(date: any): string | null {
  const dateObj = convertToDate(date)
  if (!dateObj) return null
  
  const year = dateObj.getUTCFullYear()
  const month = String(dateObj.getUTCMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getUTCDate()).padStart(2, '0')
  
  return `${year}-${month}-${day}`
}

/**
 * Formatea una fecha al formato DD/MM/YYYY usando UTC
 * 
 * @param date - Fecha a formatear
 * @returns String en formato DD/MM/YYYY o null
 */
export function formatDateDDMMYYYY(date: any): string | null {
  const dateObj = convertToDate(date)
  if (!dateObj) return null
  
  // Formato configurado por la compañía (getFormatSettings da la config activa sin
  // necesitar un componente montado). Mantiene la semántica UTC del original.
  return coreFormatDate(dateObj, getFormatSettings())
}
